import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TrackedEvents } from '@innovation-os-ng/ui/utils';

import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ZoomService } from '../zoom.service';

import * as d3 from 'd3';

export enum ZoomConfig {
  ZOOM_MIN = 0.1,
  ZOOM_MAX = 24
}

@Component({
  selector: 'chart-zoom-controls',
  templateUrl: './zoom-controls.component.html',
  styleUrls: ['./zoom-controls.component.scss']
})
export class ZoomControlsComponent implements OnInit, OnDestroy {
  // Const
  public ZOOM_IN_JUMP = 0.8;
  public ZOOM_OUT_JUMP = 1.2;

  @Input()
  zoom;

  @Input()
  padding: number;

  @Input()
  rectZoom: ElementRef;

  @Input()
  svgContainer: ElementRef;

  @Input()
  shapeWrapper: ElementRef;

  // we need to select with D3 the DOM element that will hold the zoom interaction
  private d3ZoomSelection: any;

  // we can receive via input a boolean to enable/disable zoom on scroll
  public enableZoomScrollLocal: boolean;
  @Input()
  set enableZoomScroll(enableZoomScroll: boolean) {
    this.enableZoomScrollLocal = enableZoomScroll;

    // If the zoom and the d3 selector that binds the zoom events does not exist yet we skip
    // This also allow us to skip the initialization of the input
    // we initiate the bindZoomOnScroll on init, in this set we only manage changes in the input
    if (!this.zoom || !this.d3ZoomSelection) {
      return;
    }

    this.bindZoomOnScrollEvent(this.enableZoomScrollLocal);
  }

  // Track zoom events via parent/ancestor
  @Output()
  trackEvent: EventEmitter<string> = new EventEmitter();

  /**
   * SUBSCRIPTIONS
   */
  private subs: Subscription = new Subscription();

  constructor(private readonly zoomService: ZoomService) {}

  ngOnInit() {
    this.bindZoomOnScrollEvent(this.enableZoomScrollLocal);

    setTimeout(() => {
      this.zoomToFit();
    }, 300);

    //  We manage resize as subscription to be able to debounce it
    this.subs.add(
      this.zoomService.resizeSub.pipe(debounceTime(50)).subscribe(() => {
        this.zoomToFit();
      })
    );
  }

  @HostListener('window:resize', ['$event'])
  onresize() {
    this.zoomService.resizeSub.next(null);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  //  To ZOOM IN /OUT
  public onZoom(value: number) {
    const rectZoomDom = this.rectZoom.nativeElement;

    const box = rectZoomDom.getBBox();
    const width = box.width;
    const height = box.height;

    const scaleValue = value;

    const scaleTo = d3.zoomTransform(rectZoomDom).k * scaleValue;

    if (scaleTo > ZoomConfig.ZOOM_MIN && scaleTo < ZoomConfig.ZOOM_MAX) {
      let transform = d3.zoomIdentity;

      const lastX = d3.zoomTransform(rectZoomDom).x;
      const lastY = d3.zoomTransform(rectZoomDom).y;

      const middleViewX = width / 2;
      const middleViewY = height / 2;

      const newTranslateX = middleViewX - (middleViewX - lastX) * scaleValue;
      const newTranslateY = middleViewY - (middleViewY - lastY) * scaleValue;

      if (isNaN(newTranslateX) || isNaN(newTranslateY) || isNaN(scaleTo)) {
        return;
      }

      transform = transform.translate(newTranslateX, newTranslateY);
      //  scale
      transform = transform.scale(scaleTo);

      // Add transformation to zoom
      this.zoom.transform(d3.select(this.svgContainer.nativeElement), transform);

      // Emit zoom event for tracking (the constant is mislabeled, unfortunately)
      this.trackEvent.emit(value === this.ZOOM_IN_JUMP ? TrackedEvents.ZOOM_OUT : TrackedEvents.ZOOM_IN);
    }
  }

  public zoomToFit(emitEvent?: boolean) {
    const HALF = 2;
    //  the wrapper <g>
    const wrapperDom = this.shapeWrapper.nativeElement;
    const boxWrapper = wrapperDom.getBBox();

    if (!boxWrapper || !boxWrapper.width || !boxWrapper.height) {
      return;
    }

    //  The size of the svg
    const rectZoomDom = this.rectZoom.nativeElement;
    const boxRectZoom = rectZoomDom.getBBox();

    const padding = this.padding ? this.padding : 0;

    const centerX = boxRectZoom.width / HALF;
    const centerY = boxRectZoom.height / HALF;
    const newTranslateX = -boxWrapper.x - boxWrapper.width / HALF;
    const newTranslateY = -boxWrapper.y - boxWrapper.height / HALF;
    const scaleTo = Math.min((boxRectZoom.width - padding) / boxWrapper.width, (boxRectZoom.height - padding) / boxWrapper.height);

    if (isNaN(centerX) || isNaN(centerY) || isNaN(newTranslateX) || isNaN(newTranslateY) || isNaN(scaleTo)) {
      return;
    }
    // Reset transform.
    let transform = d3.zoomIdentity;
    // Center [0, 0].
    transform = transform.translate(centerX, centerY);
    // Apply scale.
    transform = transform.scale(scaleTo);
    // Center elements.
    transform = transform.translate(newTranslateX, newTranslateY);

    // Add transformation to zoom
    this.zoom.transform(d3.select(this.svgContainer.nativeElement), transform);

    if (emitEvent) {
      // Emit zoom event for tracking
      this.trackEvent.emit(TrackedEvents.RESET_ZOOM);
    }
  }

  private bindZoomOnScrollEvent(enableZoomScroll: boolean) {
    if (!this.svgContainer || !this.svgContainer.nativeElement || !this.zoom) {
      return;
    }

    if (!this.d3ZoomSelection) {
      // We select the dom elm with D3 that creates a D3 wrapper that allow us to bind zoom events
      this.d3ZoomSelection = d3.select(this.svgContainer.nativeElement);
    } else {
      // In case the listener exist we unbind first
      this.d3ZoomSelection.on('.zoom', null);
    }

    // We init zoom with .call, but we check here if we want to bind the zoom on scroll
    if (enableZoomScroll) {
      this.d3ZoomSelection.call(this.zoom).on('dblclick.zoom', null);
      return;
    }
    // By default we don't allow zoom on scroll
    this.d3ZoomSelection.call(this.zoom).on('wheel.zoom', null).on('dblclick.zoom', null);
  }
}
